import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

interface UploadResponse {
  fileKey: string
  url: string
}

interface PreSignedUrlResponse {
  downloadUrl: string
  uploadUrl: string
  id: string
}

interface CompleteUploadResponse {
  message: string
  profile: {
    id: string
    avatarUrl: string
  }
}

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development'

/**
 * Custom hook to handle avatar uploads using the Profile Avatar API
 * @returns Mutation function and loading state
 */
export const useUploadAvatarMutation = () => {
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  /**
   * Get pre-signed URLs for upload
   * @param fileName The name of the file to upload
   * @param contentType The MIME type of the file
   * @returns Promise with pre-signed URLs and file ID
   */
  const getUploadUrl = async (
    fileName: string,
    contentType: string
  ): Promise<PreSignedUrlResponse> => {
    // Use fetchWithAuth to handle authentication automatically
    return await fetchWithAuth(`${URL}/profile/avatar/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: fileName, contentType }),
    })
  }

  /**
   * Upload file to S3 using pre-signed URL
   * @param uploadUrl The pre-signed URL for uploading
   * @param file The file to upload
   * @param contentType The MIME type of the file
   */
  const uploadToS3 = async (
    uploadUrl: string,
    file: Blob,
    contentType: string
  ): Promise<void> => {
    // Direct fetch to S3 (no auth token needed for pre-signed URL)
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error(
        `Failed to upload to S3: ${response.status} ${response.statusText}`
      )
    }
  }

  /**
   * Complete the upload process
   * @param fileId The ID of the uploaded file
   * @param fileName The name of the uploaded file
   * @param profileId Optional profile ID for admin uploads
   * @returns Promise with the complete upload response
   */
  const completeUpload = async (
    fileId: string,
    fileName: string,
    profileId?: string
  ): Promise<CompleteUploadResponse> => {
    const endpoint = profileId
      ? `${URL}/profile/${profileId}/avatar/complete`
      : `${URL}/profile/avatar/complete`

    // Use fetchWithAuth to handle authentication automatically
    return await fetchWithAuth(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ file_id: fileId, file_name: fileName }),
    })
  }

  /**
   * Upload an avatar image
   * @param file The image file to upload
   * @param profileId Optional profile ID to associate with this upload (if not the current user)
   * @returns Promise with the upload response
   */
  const uploadAvatar = async (
    file: File,
    profileId?: string
  ): Promise<UploadResponse> => {
    setLoading(true)

    // Validate file
    if (!file || !(file instanceof File)) {
      setLoading(false)
      throw new Error('Valid file is required for upload')
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLoading(false)
      throw new Error('Only image files are allowed for avatar upload')
    }

    try {
      // Step 1: Get pre-signed URLs
      const { uploadUrl, id, downloadUrl } = await getUploadUrl(
        file.name,
        file.type
      )

      // Step 2: Upload file to S3
      await uploadToS3(uploadUrl, file, file.type)

      // Step 3: Complete the upload
      const result = await completeUpload(id, file.name, profileId)

      // Invalidate relevant queries
      if (profileId) {
        // If managing a specific profile, invalidate that profile
        queryClient.invalidateQueries({ queryKey: ['profile', profileId] })
        queryClient.invalidateQueries({ queryKey: ['adminProfile', profileId] })
      } else {
        // Otherwise invalidate the current user
        queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      }

      setLoading(false)
      return {
        fileKey: id,
        url: result.profile.avatarUrl || downloadUrl,
      }
    } catch (error) {
      setLoading(false)
      console.error('Error uploading avatar:', error)
      throw error
    }
  }

  return { uploadAvatar, loading }
}
